import React, { useCallback, useContext } from 'react';
import { Link as GatsbyLink } from 'gatsby';
import { Link, type LinkProps } from '@printdeal/ui-library';
import clsx from 'clsx';
import { addDataLayerEntry } from '@printdeal/data-layer-manager';
import { dispatchDataLayerEventOnNavigationClick } from '../../helpers';
import { HeaderContext } from '../context';

type NavigationLinkProps = Omit<LinkProps<typeof GatsbyLink>, 'to' | 'key'> & {
  key?: string;
  to?: string;
  navigationType: 'main' | 'featured' | 'supplementary';
  navigationLevel?: 'main_Level1' | 'level2' | 'level3';
  eventLabel?: string;
};

const NavigationLink = ({
  key,
  to,
  children,
  className,
  onClick,
  variant = 'secondary',
  navigationType,
  navigationLevel,
  eventLabel,
  ...rest
}: NavigationLinkProps) => {
  const { isSticky } = useContext(HeaderContext);

  const handleClick = useCallback<React.MouseEventHandler<HTMLAnchorElement>>((event) => {
    if (eventLabel) {
      // GA3.
      dispatchDataLayerEventOnNavigationClick({
        navType: isSticky ? `navigation_${navigationType}_sticky` : `navigation_${navigationType}`,
        eventLabel,
      });
      // GA4.
      addDataLayerEntry(`navigation_${navigationType}_click`, {
        // Web-analist wants category_name as key for main and featured navigations.
        [navigationType === 'supplementary' ? 'label' : 'category_name']: eventLabel,
        level: navigationLevel,
      });
    }

    onClick?.(event);
  }, [navigationType, navigationLevel, eventLabel, onClick, isSticky]);

  if (!children) {
    return null;
  }

  if (!to) {
    return (
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <Link
        as="span"
        key={key}
        variant={variant}
        className={clsx(className || 'pd-text-dim-800')}
        {...rest}
      >
        {children}
      </Link>
    );
  }

  return (
    <Link
      as={GatsbyLink}
      key={key}
      to={to}
      variant={variant}
      className={className}
      onClick={handleClick}
      state={{ redirectUrl: to }}
      {...rest}
    >
      {children}
    </Link>
  );
};

export default NavigationLink;
